// The v4 comparison as a client report (Drew, 2026-11-04: "make it look professional,
// like you see what we did with the advisor prism"). Drawn with jsPDF as real text and
// vector shapes, never a screenshot, so it is sharp at any zoom. Every figure comes from
// the same helpers the v4 page renders (planLedger, runningTotals, winnerSentence), so
// paper and screen cannot disagree. Pages: the answer and the chart; strategy by
// strategy, as many to a page as fit; this year's return and the ledger; every alt side
// by side. Only the strategies the advisor ticked are printed, under their on-screen
// numbers and colours.
import jsPDF from "jspdf";
import type { TaxScenario, TaxResults } from "@/tax/types";
import {
  bracketReading, buildStrategyPlan, costBenefitTitle, isBaselinePlusAlt, planLedger, roundAxis, runningTotals, wholeAgain, winnerSentence,
  type PlanLedger, type StrategyPlan,
} from "@/lib/comparisonStory";
import { payoffHorizonYears } from "@/tax/strategyJourney";
import { formatCurrency, formatMultiple } from "@/tax/format";
import { CYA_TEXT, altFundDisclosure } from "@/lib/disclaimer";

type RGB = [number, number, number];
function hsl(h: number, s: number, l: number): RGB {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}
const INK = hsl(222, 47, 11);
const MUTED = hsl(220, 9, 46);
const SOFT = hsl(220, 9, 60);
const RULE = hsl(220, 13, 88);
const HAIR = hsl(220, 13, 93);
const NAVY = hsl(212, 42, 24);
const NAVY_2 = hsl(212, 42, 30);
const NAVY_LINE = hsl(212, 30, 40);
const GOOD = hsl(152, 56, 30);
const BAD = hsl(0, 72, 45);
const TINT = hsl(212, 40, 97);
const BOX_BG = hsl(207, 60, 97);
const BOX_LINE = hsl(207, 50, 88);
const BOX_HEAD = hsl(207, 70, 40);
const BASE_COLOR = hsl(220, 9, 46);
const COLORS: RGB[] = [hsl(212, 42, 30), hsl(207, 90, 54), hsl(152, 56, 40), hsl(38, 92, 45), hsl(280, 45, 45)];
const colorOf = (i: number) => (i < 0 ? BASE_COLOR : COLORS[i % COLORS.length]);

const money = (v: number) => formatCurrency(Math.abs(v));
const neg = (v: number) => (Math.round(v) < 0 ? `–${money(v)}` : money(v));
const plain = (v: number) => `${Math.round(v) < 0 ? "–" : ""}${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
const compact = (v: number) => {
  const a = Math.abs(v);
  const sign = v < 0 ? "–" : "+";
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  return `${sign}$${Math.round(a / 1_000)}K`;
};
const tick = (v: number) => {
  const a = Math.abs(v);
  const s = a >= 1_000_000 ? `$${(a / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0)}M` : a >= 1_000 ? `$${Math.round(a / 1_000)}K` : `$${a.toFixed(0)}`;
  return v < 0 ? `–${s}` : s;
};
const suffix = (name: string) => name.match(/\(([^)]+)\)\s*$/)?.[1] ?? name;
const client = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, "") || name;
const FILING: Record<string, string> = { S: "Single", MFJ: "Married filing jointly", MFS: "Married filing separately", HOH: "Head of household", QW: "Qualifying surviving spouse" };

// Page geometry, in points (letter, portrait).
const PW = 612, PH = 792, M = 50, CW = PW - 2 * M;
const TOP = 74; // where content starts on pages 2+, under the running header
const FOOT = PH - 58; // footer rule

/**
 * Build the report. `all` is every scenario on the page, baseline first; `picked` the
 * strategies the advisor ticked, as indexes into `all.slice(1)` (every one when omitted).
 */
export function buildComparisonReport(all: TaxScenario[], withoutAlt: Record<string, TaxResults>, picked?: number[]): jsPDF {
  const idx = (picked ?? all.slice(1).map((_, i) => i)).filter((i) => i >= 0 && i < all.length - 1).sort((a, b) => a - b);
  if (idx.length === 0) throw new Error("Pick at least one strategy to export.");
  const base = all[0];
  const strategies = idx.map((i) => all[i + 1]);
  const scenarios = [base, ...strategies];
  const col = (i: number) => colorOf(idx[i]);
  const H = payoffHorizonYears(all); // the page's horizon, whichever strategies are ticked
  const plans = strategies.map((s) => buildStrategyPlan(s, base, isBaselinePlusAlt(s, base) ? base.results : withoutAlt[s.id] ?? null, H));
  const ledgers = plans.map(planLedger);
  const totals = runningTotals(plans, base);
  const names = strategies.map((s, i) => `Strategy ${idx[i] + 1} · ${suffix(s.input.name)}`);
  const endYear = totals.labels[totals.labels.length - 1];
  const paidYear = totals.labels[totals.medicareIndex];
  const winner = ledgers.reduce((b, l, i) => (l.aheadBy > ledgers[b].aheadBy ? i : b), 0);
  const taxYear = base.input.taxYear;
  const who = client(base.input.name);
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setProperties({ title: `${who} · Tax Strategy Comparison` });
  const fill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const draw = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
  const ink = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
  const font = (size: number, style: "normal" | "bold" = "normal", c: RGB = INK) => { doc.setFont("helvetica", style); doc.setFontSize(size); ink(c); };
  const hr = (y: number, c: RGB = RULE, w = 0.6, x0 = M, x1 = M + CW) => { draw(c); doc.setLineWidth(w); doc.line(x0, y, x1, y); };
  const right = (t: string, x: number, y: number) => doc.text(t, x, y, { align: "right" });
  const caps = (t: string, x: number, y: number, c: RGB = MUTED, size = 7.5) => { font(size, "bold", c); doc.setCharSpace(0.9); doc.text(t.toUpperCase(), x, y); doc.setCharSpace(0); };
  const sectionTitle = (t: string, sub: string | null, y: number) => {
    font(13, "bold"); doc.text(t, M, y);
    if (sub) { font(8.5, "normal", MUTED); right(sub, M + CW, y); }
    hr(y + 7, INK, 0.9);
    return y + 24;
  };
  const arrow = (x: number, y: number, c: RGB) => { draw(c); doc.setLineWidth(0.8); doc.line(x, y - 2.6, x + 6, y - 2.6); fill(c); doc.triangle(x + 6, y - 4.6, x + 6, y - 0.6, x + 8.6, y - 2.6, "F"); };

  // Rich text with word wrap: segments of different colour/weight on shared lines.
  type Seg = { t: string; c?: RGB; b?: boolean };
  const rich = (segs: Seg[], x: number, y: number, maxW: number, size: number, lh: number) => {
    const words: { w: string; c: RGB; b: boolean }[] = [];
    segs.forEach((s) => s.t.split(/(\s+)/).filter((w) => w !== "").forEach((w) => words.push({ w, c: s.c ?? [255, 255, 255], b: !!s.b })));
    let cx = x, cy = y;
    for (const w of words) {
      doc.setFont("helvetica", w.b ? "bold" : "normal"); doc.setFontSize(size);
      const wd = doc.getTextWidth(w.w);
      if (/^\s+$/.test(w.w)) { if (cx > x) cx += wd; continue; }
      if (cx + wd > x + maxW && cx > x) { cx = x; cy += lh; }
      ink(w.c); doc.text(w.w, cx, cy); cx += wd;
    }
    return cy;
  };

  // ── Page 1: title block ───────────────────────────────────────────────────
  fill(NAVY); doc.rect(0, 0, PW, 6, "F");
  caps("Tax Strategy Comparison", M, 36, BOX_HEAD, 7.5);
  font(8, "normal", MUTED); right(`Prepared ${date}`, M + CW, 36);
  font(19, "bold"); doc.text(who, M, 58);
  const i0 = base.input;
  const facts = [FILING[i0.filingStatus] ?? i0.filingStatus];
  if (i0.filingStatus === "MFJ" && i0.tp65Plus && i0.sp65Plus) facts.push("both 65 or older");
  if (i0.state) facts.push(i0.state);
  const wages = (i0.wages || 0) + (i0.filingStatus === "MFJ" ? i0.spouseWages || 0 : 0);
  if (wages > 0) facts.push(`${money(wages)} of W-2 wages`);
  facts.push(`Tax year ${taxYear}`);
  facts.push(`Baseline vs ${plans.length} ${plans.length === 1 ? "strategy" : "strategies"}`);
  font(8.8, "normal", MUTED); doc.text(facts.join("  ·  "), M, 73);
  hr(84, RULE, 0.7);

  // ── The answer, compact ───────────────────────────────────────────────────
  const L = ledgers[winner];
  const ahead = L.aheadBy >= 0;
  const whole = wholeAgain(totals.lines[winner], totals.baseline, totals.labels);
  let y = 98;
  const boxTop = y;
  const sentence = winnerSentence(plans[winner], `Strategy ${idx[winner] + 1}`, endYear);
  const textW = CW - 36;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.6);
  const sLines = doc.splitTextToSize(sentence, textW);
  const head: Seg[] = ahead
    ? [{ t: `${names[winner]} puts them `, b: true }, { t: money(L.aheadBy), c: hsl(152, 60, 66), b: true }, { t: ` ahead of Baseline by ${endYear}.`, b: true }]
    : [{ t: `${names[winner]} keeps them closest to Baseline: `, b: true }, { t: money(L.aheadBy), c: hsl(0, 85, 78), b: true }, { t: ` behind by ${endYear}.`, b: true }];
  // measure the headline's lines by drawing it off-page first
  const hLinesEnd = rich(head, M + 18, -1000, textW, 13, 16.5);
  const hLines = Math.round((hLinesEnd + 1000) / 16.5) + 1;
  const cellsH = 48;
  const boxH = 22 + hLines * 16.5 + 4 + sLines.length * 11.5 + 10 + cellsH + 16;
  fill(NAVY); doc.roundedRect(M, boxTop, CW, boxH, 6, 6, "F");
  caps("The answer", M + 18, y + 17, hsl(207, 80, 80), 6.8);
  const hy = rich(head, M + 18, y + 34, textW, 13, 16.5);
  font(8.6, "normal", hsl(210, 30, 86));
  doc.text(sLines, M + 18, hy + 15, { lineHeightFactor: 1.33 });
  const cy = hy + 15 + sLines.length * 11.5 + 4;
  const cellW = textW / 3;
  const cells = [
    { label: L.outThisYear < 0 ? "Ahead this year" : "Out this year", value: money(L.outThisYear), sub: L.altCheck > 0 ? "tax change + alt check" : "tax change" },
    { label: `Back by ${endYear}`, value: money(L.fundCash + L.futureTaxAvoided), sub: [L.fundCash > 0 ? "alt cash" : "", plans[winner].levers.roth ? "taxes avoided" : ""].filter(Boolean).join(" + ") || "nothing comes back" },
    { label: "Whole again", value: whole.kind === "year" ? whole.label : whole.kind === "never-behind" ? "From day one" : `Not by ${endYear}`, sub: L.altCheck > 0 ? `${money(L.altCheck)} at risk the whole way` : "against doing nothing" },
  ];
  draw(NAVY_LINE); doc.setLineWidth(0.7); doc.roundedRect(M + 18, cy, textW, cellsH, 4, 4, "S");
  cells.forEach((c, k) => {
    const x = M + 18 + k * cellW;
    if (k > 0) { draw(NAVY_LINE); doc.line(x, cy, x, cy + cellsH); }
    font(7, "normal", hsl(210, 30, 80)); doc.text(c.label, x + 10, cy + 12);
    font(12.5, "bold", [255, 255, 255]); doc.text(c.value, x + 10, cy + 28);
    font(6.6, "normal", hsl(210, 30, 76)); doc.text(c.sub, x + 10, cy + 39);
  });
  y = boxTop + boxH + 24;

  // ── The chart, given the rest of the page ─────────────────────────────────
  y = sectionTitle(costBenefitTitle(H), "Running total of the client's money, every strategy against Baseline", y);
  const n = totals.labels.length, last = n - 1;
  const allVals = [...totals.baseline, ...totals.lines.flat()];
  // Five to seven even steps, whatever the client's size.
  const lo = Math.min(0, ...allVals), hi = Math.max(0, ...allVals);
  const STEPS = [10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000, 10_000_000];
  const step = STEPS.find((st) => (Math.ceil(hi / st) - Math.floor(lo / st)) <= 7) ?? STEPS[STEPS.length - 1];
  const axis = { min: Math.floor(lo / step) * step, max: Math.ceil(hi / step) * step, ticks: [] as number[] };
  for (let t = axis.min; t <= axis.max + 1; t += step) axis.ticks.push(t);
  const cx0 = M + 44, cx1 = M + CW - 132, cTop = y + 6, cBot = cTop + 190;
  const X = (i: number) => cx0 + (i * (cx1 - cx0)) / last;
  const Y = (v: number) => cTop + ((axis.max - v) / (axis.max - axis.min)) * (cBot - cTop);
  fill(hsl(220, 14, 96)); doc.rect(X(0), cTop, X(1) - X(0), cBot - cTop, "F");
  axis.ticks.forEach((t) => {
    hr(Y(t), t === 0 ? hsl(220, 13, 72) : HAIR, t === 0 ? 0.8 : 0.5, cx0, cx1);
    font(7.5, "normal", MUTED); right(tick(t), cx0 - 6, Y(t) + 2.5);
  });
  totals.labels.forEach((l, i) => { font(7.5, i < 2 ? "bold" : "normal", i < 2 ? INK : MUTED); doc.text(l, X(i), cBot + 13, { align: "center" }); });
  const poly = (vals: number[], c: RGB, w: number, dash = false) => {
    draw(c); doc.setLineWidth(w);
    if (dash) doc.setLineDashPattern([3.5, 2.5], 0);
    for (let i = 1; i < vals.length; i++) doc.line(X(i - 1), Y(vals[i - 1]), X(i), Y(vals[i]));
    if (dash) doc.setLineDashPattern([], 0);
  };
  poly(totals.baseline, BASE_COLOR, 1.3, true);
  totals.lines.forEach((ln, i) => poly(ln, col(i), 2));
  totals.lines.forEach((ln, i) => { fill(col(i)); doc.circle(X(last), Y(ln[last]), 2.4, "F"); });
  let boxes = [
    { v: totals.baseline[last], value: compact(-base.results.totalTaxLiability), vc: INK, name: "Baseline", c: BASE_COLOR },
    ...ledgers.map((l, i) => ({ v: totals.lines[i][last], value: compact(l.aheadBy), vc: l.aheadBy >= 0 ? GOOD : BAD, name: names[i], c: col(i) })),
  ].map((b) => ({ ...b, y: Y(b.v) })).sort((a, b) => a.y - b.y);
  boxes = boxes.reduce<typeof boxes>((acc, b) => { const p = acc[acc.length - 1]; acc.push(p && b.y - p.y < 30 ? { ...b, y: p.y + 30 } : b); return acc; }, []);
  const over = Math.max(0, boxes[boxes.length - 1].y + 13 - cBot);
  if (over) boxes = boxes.map((b) => ({ ...b, y: b.y - over }));
  boxes.forEach((b) => {
    const bx = cx1 + 10, bw = 122, bh = 26, by = b.y - bh / 2;
    fill([255, 255, 255]); draw(RULE); doc.setLineWidth(0.6); doc.roundedRect(bx, by, bw, bh, 3, 3, "FD");
    fill(b.c); doc.rect(bx, by, 2.5, bh, "F");
    font(9.5, "bold", b.vc); doc.text(b.value, bx + 8, by + 11);
    font(6.8, "normal", MUTED); doc.text(doc.splitTextToSize(b.name, bw - 12)[0], bx + 8, by + 21);
  });
  y = cBot + 30;
  font(7.8, "normal", MUTED);
  const note = `The dashed line is Baseline: doing nothing still pays ${money(base.results.totalTaxLiability)} of tax this year. Today is the alt check going out; Tax day adds the year's tax; each year after adds the fund's cash back, the Medicare change in the year it is paid, and the Roth's future taxes avoided at ${endYear}.`;
  doc.text(doc.splitTextToSize(note, CW), M, y, { lineHeightFactor: 1.4 });
  // ── Strategy by strategy: as many to a page as fit, never split ───────────
  doc.addPage(); y = TOP;
  y = sectionTitle("Strategy by strategy", "This year, and where each one leaves them by " + endYear, y);
  y += 2;
  plans.forEach((plan, i) => {
    const Lx = ledgers[i];
    const s = strategies[i];
    const c = col(i);
    const otherMoves = Boolean(plan.levers.roth || plan.levers.ltcg);
    type R = { label: string; value: string; color?: RGB; bold?: boolean; rule?: boolean; arrow?: boolean };
    const left: R[] = [];
    if (plan.levers.roth > 0) left.push({ label: "Roth conversion", value: money(plan.levers.roth) });
    if (plan.levers.ltcg !== 0) left.push({ label: "Long-term capital gains harvested", value: money(plan.levers.ltcg) });
    if (Lx.altCheck > 0) left.push({ label: "Alt deduction", value: money(Lx.altDeduction) });
    if (Lx.altCheck > 0) left.push({ label: "Total deduction, after the 2/37 limit", value: money(s.results.totalDeduction) });
    left.push({ label: "", value: "", rule: true });
    if (Lx.altCheck > 0 && Lx.pending) {
      // The without-alt run has not come back: one combined line, as on the page.
      left.push({ label: "Tax change, with the deduction inside it", value: neg(Lx.taxChange), color: Lx.taxChange > 0 ? BAD : GOOD });
    } else if ((Lx.altCheck === 0 || otherMoves) && Lx.strategyTax !== null) {
      const tl = plan.levers.roth && plan.levers.ltcg ? "Tax on the conversion and the gains" : plan.levers.roth ? "Tax on the conversion" : plan.levers.ltcg ? "Tax on the gains" : "Tax change";
      left.push({ label: tl, value: neg(Lx.strategyTax), color: Lx.strategyTax > 0 ? BAD : GOOD });
    }
    if (Lx.altCheck > 0) left.push({ label: "Alt investment", value: money(Lx.altCheck), color: BAD });
    if (Lx.altCheck > 0 && Lx.scheduleAOffset !== null) left.push({ label: "Taxes reduced by Schedule A", value: Lx.scheduleAOffset <= 0 ? `–${money(Lx.scheduleAOffset)}` : money(Lx.scheduleAOffset), color: GOOD });
    left.push({ label: "", value: "", rule: true });
    left.push({ label: Lx.outThisYear < 0 ? "Net ahead this year" : "Net out of pocket", value: money(Lx.outThisYear), bold: true });
    const rr: R[] = [];
    rr.push({ label: Lx.outThisYear < 0 ? "Net ahead this year" : "Net out of pocket this year", value: money(Lx.outThisYear), color: Lx.outThisYear > 0 ? BAD : GOOD });
    if (Lx.medicare !== 0) rr.push({ label: `Medicare (IRMAA) ${Lx.medicare > 0 ? "increase" : "decrease"}, paid ${paidYear}`, value: money(Lx.medicare), color: Lx.medicare > 0 ? BAD : GOOD });
    if (Lx.altCheck > 0) rr.push({ label: "Cash back from the alt", value: money(Lx.fundCash), color: GOOD, arrow: true });
    if (plan.levers.roth !== 0) rr.push({ label: "Future taxes avoided", value: money(Lx.futureTaxAvoided), color: Lx.futureTaxAvoided >= 0 ? GOOD : BAD, arrow: true });
    rr.push({ label: "", value: "", rule: true });
    rr.push({ label: `${Lx.aheadBy >= 0 ? "Ahead" : "Behind"} by ${endYear}`, value: money(Lx.aheadBy), color: Lx.aheadBy >= 0 ? GOOD : BAD, bold: true });
    if (plan.levers.roth !== 0) { rr.push({ label: "", value: "", rule: true }); rr.push({ label: "Roth balance, tax-free", value: money(plan.rothFutureValue) }); }

    const RH = 14.5, RB = 19, RR = 9;
    const rowsH = (rows: R[]) => rows.reduce((h, r) => h + (r.rule ? RR : r.bold ? RB : RH), 0);
    const panelH = Math.max(rowsH(left), rowsH(rr)) + 52;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const story = doc.splitTextToSize(winnerSentence(plan, `Strategy ${idx[i] + 1}`, endYear), CW);
    const blockH = 24 + story.length * 12.5 + 10 + panelH + 26;
    if (y + blockH > FOOT - 8) { doc.addPage(); y = TOP; }

    fill(c); doc.roundedRect(M, y - 10, 10, 10, 2, 2, "F");
    font(12.5, "bold"); doc.text(names[i], M + 17, y - 1);
    hr(y + 6, c, 1.2);
    y += 21;
    font(9, "normal", INK); doc.text(story, M, y, { lineHeightFactor: 1.38 });
    y += story.length * 12.5 + 6;

    const gap = 14, pw = (CW - gap) / 2, px = M, qx = M + pw + gap;
    fill(BOX_BG); draw(BOX_LINE); doc.setLineWidth(0.8); doc.roundedRect(px, y, pw, panelH, 5, 5, "FD");
    draw(RULE); doc.roundedRect(qx, y, pw, panelH, 5, 5, "S");
    const paint = (rows: R[], x: number, w: number, title: string, tc: RGB, foot: string) => {
      caps(title, x + 12, y + 18, tc, 7);
      let yy = y + 35;
      rows.forEach((r) => {
        if (r.rule) { hr(yy - 5, BOX_LINE, 0.7, x + 12, x + w - 12); yy += RR; return; }
        const size = r.bold ? 10.5 : 8.6;
        font(size, r.bold ? "bold" : "normal", r.bold ? INK : MUTED);
        let lx = x + 12;
        if (r.arrow) { arrow(lx, yy, MUTED); lx += 12; }
        doc.text(r.label, lx, yy);
        font(size, r.bold ? "bold" : "normal", r.color ?? INK); right(r.value, x + w - 12, yy);
        yy += r.bold ? RB : RH;
      });
      font(7, "normal", MUTED); doc.text(doc.splitTextToSize(foot, w - 24), x + 12, y + panelH - 14, { lineHeightFactor: 1.3 });
    };
    paint(left, px, pw, `This year · the ${taxYear} return`, BOX_HEAD, `Total tax this year ${money(s.results.totalTaxLiability)} · Baseline ${money(base.results.totalTaxLiability)}`);
    paint(rr, qx, pw, `By ${endYear}`, MUTED,
      [Lx.altCheck > 0 ? `Capital at risk the whole way: ${money(Lx.altCheck)}.` : "", plan.levers.roth !== 0 ? "Future taxes avoided = Roth balance × this year's effective rate." : ""].filter(Boolean).join(" "));
    y += panelH + 30;
  });

  // ── Page 2: the return and the ledger ─────────────────────────────────────
  doc.addPage(); y = TOP;
  y = sectionTitle(`This year · the ${taxYear} return`, "Change against Baseline under each strategy's number", y);
  const labelW = 150;
  const colW = (CW - labelW) / scenarios.length;
  const colR = (k: number) => M + labelW + colW * (k + 1) - 4;
  // Column headings: a colour rule, "STRATEGY 2" in small capitals, then the nickname
  // broken only between words, every column starting on the same line.
  const nickLines = (i: number) => { doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); return doc.splitTextToSize(suffix(strategies[i].input.name), colW - 10) as string[]; };
  const headRows = Math.max(1, ...strategies.map((_, i) => nickLines(i).length));
  const heads = (top: number) => {
    const capsY = top + 2;
    const tracked = (t: string, c: RGB, xr: number) => { font(6.8, "bold", c); doc.setCharSpace(0.7); right(t.toUpperCase(), xr, capsY); doc.setCharSpace(0); };
    tracked("Baseline", MUTED, colR(0));
    strategies.forEach((_, i) => {
      const xr = colR(i + 1);
      draw(col(i)); doc.setLineWidth(1.6); doc.line(xr - (colW - 10), top - 8, xr, top - 8);
      tracked(`Strategy ${idx[i] + 1}`, col(i), xr);
      font(8.5, "bold", INK);
      nickLines(i).forEach((ln, k) => right(ln, xr, capsY + 12 + k * 10.5));
    });
    const bottom = capsY + 12 + (headRows - 1) * 10.5 + 7;
    hr(bottom, INK, 0.8);
    return bottom + 4;
  };
  y = heads(y + 6);
  // The change against Baseline: grey, an arrow and the amount, never red or green (as on the page).
  const badge = (cur: number, b0: number, xr: number, yy: number) => {
    const d = Math.round(cur) - Math.round(b0);
    if (d === 0) { font(7, "normal", SOFT); right("no change", xr, yy); return; }
    const t = money(d);
    font(7, "bold", MUTED);
    const w = doc.getTextWidth(t) + 13;
    fill(hsl(220, 14, 94)); doc.roundedRect(xr - w - 1, yy - 7.2, w + 3, 9.6, 2, 2, "F");
    right(t, xr, yy);
    fill(MUTED);
    const tx = xr - w + 3.5;
    if (d > 0) doc.triangle(tx, yy - 1, tx + 5, yy - 1, tx + 2.5, yy - 5.2, "F"); else doc.triangle(tx, yy - 5.2, tx + 5, yy - 5.2, tx + 2.5, yy - 1, "F");
  };
  const row = (label: string, get: (r: TaxResults) => number, tone: "context" | "tax" | "total") => {
    const h = 30;
    if (tone === "total") { fill(TINT); doc.rect(M, y, CW, h, "F"); }
    font(tone === "total" ? 10.5 : 9.5, tone === "context" ? "normal" : "bold", tone === "context" ? MUTED : INK);
    doc.text(label, M + 6, y + 13);
    scenarios.forEach((s, k) => {
      font(tone === "total" ? 10.5 : 9.5, tone === "total" ? "bold" : "normal", tone === "context" ? MUTED : INK);
      right(money(get(s.results)), colR(k), y + 13);
      if (k > 0) badge(get(s.results), get(base.results), colR(k), y + 25);
    });
    y += h;
    if (tone !== "total") hr(y, HAIR, 0.5);
  };
  row("Income", (r) => r.adjustedGrossIncome, "context");
  row("Deductions", (r) => r.totalDeduction, "context");
  row("Taxable income", (r) => r.taxableIncome, "context");
  hr(y, hsl(220, 13, 80), 1);
  row("Federal tax", (r) => r.totalFederalTax, "tax");
  row(`${base.input.state || "State"} tax`, (r) => r.stateIncomeTax, "tax");
  row("Total tax this year", (r) => r.totalTaxLiability, "total");
  // Bracket row
  {
    const h = 34;
    font(9.5, "normal", MUTED); doc.text("Federal bracket on", M + 6, y + 13); doc.text("ordinary income", M + 6, y + 24);
    scenarios.forEach((s, k) => {
      const b = bracketReading(s.results);
      const xr = colR(k);
      if (!b) { font(9, "normal", SOFT); right("—", xr, y + 13); return; }
      font(b.kind === "none" ? 7.5 : 9.5, "bold"); right(b.kind === "none" ? "None" : b.rate, xr, y + 12);
      font(7, "normal", MUTED); right(b.kind === "in" ? `${money(b.room)} left` : b.kind === "top" ? "top bracket" : "all at gains rates", xr, y + 21);
      const bw = colW - 16;
      fill(hsl(220, 14, 92)); doc.roundedRect(xr - bw, y + 25, bw, 3.5, 1.7, 1.7, "F");
      if (b.kind !== "none") { fill((k === 0 ? BASE_COLOR : col(k - 1))); doc.roundedRect(xr - bw, y + 25, Math.max(2, (b.pct / 100) * bw), 3.5, 1.7, 1.7, "F"); }
    });
    y += h; hr(y, HAIR, 0.5);
  }
  const strategiesOnly = scenarios.slice(1);
  if (strategiesOnly.some((s) => Math.abs((s.results.totalMedicarePremium || 0) - (base.results.totalMedicarePremium || 0)) >= 0.5)) {
    const mp = (r: TaxResults) => (r.totalMedicarePremium || 0) * 12;
    const h = 30;
    font(9.5, "normal", MUTED); doc.text(`Medicare in ${paidYear},`, M + 6, y + 13); doc.text("set by this return", M + 6, y + 24);
    scenarios.forEach((s, k) => { font(9.5, "normal", INK); right(money(mp(s.results)), colR(k), y + 13); if (k > 0) badge(mp(s.results), mp(base.results), colR(k), y + 25); });
    y += h; hr(y, HAIR, 0.5);
  }

  y += 30;
  y = sectionTitle("What each strategy does", "Each move carries its own tax; every column adds top to bottom", y);
  y = heads(y + 6);
  type Cell = number | null;
  const ledger = (label: string, get: (l: PlanLedger, p: StrategyPlan) => Cell, opts: { total?: boolean; result?: boolean } = {}) => {
    const h = opts.result ? 24 : 19;
    if (opts.total) hr(y, INK, opts.result ? 1.2 : 0.9);
    if (opts.result) { fill(TINT); doc.rect(M, y + 0.6, CW, h, "F"); }
    font(opts.result ? 10.5 : 9.5, opts.total ? "bold" : "normal", INK); doc.text(label, M + 6, y + (opts.result ? 16 : 13));
    font(9.5, "normal", RULE); right("—", colR(0), y + 13);
    ledgers.forEach((Lx, i) => {
      const v = get(Lx, plans[i]);
      const xr = colR(i + 1), yy = y + (opts.result ? 16 : 13);
      if (v === null) { font(9.5, "normal", RULE); right("—", xr, yy); return; }
      font(opts.result ? 10.5 : 9.5, opts.total ? "bold" : "normal", opts.result ? (v < 0 ? BAD : GOOD) : INK);
      right(plain(v), xr, yy);
    });
    y += h;
  };
  caps("This year", M + 6, y + 10); y += 16;
  ledger("Strategy tax", (l) => l.strategyTax);
  ledger("Schedule A offset", (l) => l.scheduleAOffset);
  ledger("Tax change", (l) => l.taxChange, { total: true });
  ledger("Alt investment", (l) => l.altCheck || null);
  ledger("Out of pocket", (l) => l.outThisYear, { total: true });
  y += 8; caps(`By ${endYear}`, M + 6, y + 10); y += 16;
  ledger("Fund cash back", (l) => (l.altCheck > 0 ? l.fundCash : null));
  ledger("Future taxes avoided", (l, p) => (p.levers.roth !== 0 ? l.futureTaxAvoided : null));
  ledger(`Medicare, paid ${paidYear}`, (l) => (l.medicare ? -l.medicare : null));
  ledger(`Ahead by ${endYear}`, (l) => l.aheadBy, { total: true, result: true });


  // ── The investments, side by side ─────────────────────────────────────────
  const alts = plans.map((p, i) => ({ alt: p.alt, i })).filter((x): x is { alt: NonNullable<StrategyPlan["alt"]>; i: number } => x.alt !== null);
  if (alts.length) {
    doc.addPage(); y = TOP;
    const labelsSet = [...new Set(alts.map((a) => a.alt.label))];
    const exhibit = labelsSet.reduce((a, b) => (b.length < a.length ? b : a));
    y = sectionTitle(alts.length === 1 ? "The investment on its own" : "The investments, side by side", exhibit, y);
    font(8.5, "normal", MUTED); doc.text("The alt alone, with no tax dollars mixed in. Each schedule is the fund's per-$100,000 pro forma scaled to that strategy's check.", M, y - 4);
    y += 14;
    // Summary table
    const cols = [{ k: "Strategy", w: 170, a: "left" }, { k: "Capital at risk", w: 72 }, { k: "Cash back", w: 72 }, { k: "Multiple", w: 52 }, { k: "IRR", w: 52 }, { k: "Biggest year", w: CW - 170 - 72 - 72 - 52 - 52 }];
    let cx = M; const xs = cols.map((c) => { const x = cx; cx += c.w; return x; });
    cols.forEach((c, k) => { caps(c.k, k === 0 ? xs[k] + 4 : xs[k] + c.w - 4 - (doc.getTextWidth(c.k.toUpperCase()) + c.k.length * 0.9), y, MUTED, 6.8); });
    y += 6; hr(y, INK, 0.8); y += 2;
    alts.forEach(({ alt, i }) => {
      const h = 22;
      fill(col(i)); doc.roundedRect(xs[0] + 4, y + 7.5, 7, 7, 1.5, 1.5, "F");
      font(9, "bold", INK); doc.text(names[i], xs[0] + 16, y + 14);
      const by = alt.biggestYear ? `${totals.labels[alt.biggestYear + 1] ?? `Yr ${alt.biggestYear}`} · ${money(alt.biggestAmount)}` : "—";
      const vals = [money(alt.capital), money(alt.cashBack), formatMultiple(alt.moic), `${(alt.irr * 100).toFixed(1)}%`, by];
      vals.forEach((v, k) => { font(9, k < 2 ? "bold" : "normal", INK); right(v, xs[k + 1] + cols[k + 1].w - 4, y + 14); });
      y += h; hr(y, HAIR, 0.5);
    });
    y += 26;

    // Grouped bars: each year, one bar per strategy
    caps("Cash back by year", M, y, INK, 7.5);
    alts.forEach(({ i }, k) => {
      const lx = M + CW - (alts.length - k) * 120;
      fill(col(i)); doc.roundedRect(lx, y - 6.5, 7, 7, 1.5, 1.5, "F");
      font(7, "normal", MUTED); doc.text(doc.splitTextToSize(names[i], 108)[0], lx + 10, y);
    });
    y += 14;
    const years = totals.labels.slice(2, 2 + Math.max(...alts.map((a) => a.alt.flows.length)));
    const peak = Math.max(1, ...alts.flatMap((a) => a.alt.flows));
    const ax0 = M + 44, bTop = y + 6, bBot = y + 170;
    const gw = (M + CW - ax0) / years.length;
    const ticks = roundAxis(0, peak).ticks.filter((t) => t >= 0);
    const maxT = Math.max(...ticks);
    ticks.forEach((t) => { const yy = bBot - (t / maxT) * (bBot - bTop); hr(yy, t === 0 ? hsl(220, 13, 72) : HAIR, t === 0 ? 0.8 : 0.5, ax0, M + CW); font(7, "normal", MUTED); right(tick(t), ax0 - 6, yy + 2.5); });
    const inner = gw - 14, bw = inner / alts.length;
    years.forEach((yl, k) => {
      alts.forEach(({ alt, i }, j) => {
        const v = alt.flows[k] ?? 0;
        const h = Math.max(v > 0 ? 1.5 : 0, (v / maxT) * (bBot - bTop));
        fill(col(i)); doc.rect(ax0 + k * gw + 7 + j * bw, bBot - h, bw - 1.5, h, "F");
      });
      font(7.5, "normal", MUTED); doc.text(yl, ax0 + k * gw + gw / 2, bBot + 12, { align: "center" });
    });
    y = bBot + 34;

    // The numbers behind the bars
    caps("The schedule, year by year", M, y, INK, 7.5); y += 8;
    const firstW = 150, yw = (CW - firstW - 70) / years.length;
    hr(y, INK, 0.8); y += 12;
    font(7.5, "bold", MUTED); years.forEach((yl, k) => right(yl, M + firstW + yw * (k + 1), y)); right("Total", M + CW, y);
    y += 6; hr(y, RULE, 0.5);
    alts.forEach(({ alt, i }) => {
      y += 15;
      fill(col(i)); doc.roundedRect(M + 2, y - 7, 6, 6, 1.5, 1.5, "F");
      font(7.8, "bold", INK); doc.text(doc.splitTextToSize(names[i], firstW - 16)[0], M + 13, y - 1);
      years.forEach((_, k) => { font(7.5, "normal", INK); right(alt.flows[k] ? Math.round(alt.flows[k]).toLocaleString("en-US") : "—", M + firstW + yw * (k + 1), y - 1); });
      font(7.8, "bold", INK); right(money(alt.cashBack), M + CW, y - 1);
      y += 5; hr(y, HAIR, 0.5);
    });
    y += 22;
    fill(hsl(38, 90, 96)); draw(hsl(38, 70, 82)); doc.setLineWidth(0.6);
    font(8, "normal", hsl(30, 40, 30));
    const disc = doc.splitTextToSize(altFundDisclosure(), CW - 24);
    doc.roundedRect(M, y, CW, disc.length * 11 + 16, 4, 4, "FD");
    doc.text(disc, M + 12, y + 14, { lineHeightFactor: 1.35 });
  }

  // ── Running header and footer on every page ───────────────────────────────
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    if (p > 1) {
      fill(NAVY); doc.rect(0, 0, PW, 4, "F");
      font(8, "bold"); doc.text(who, M, 32);
      font(8, "normal", MUTED); doc.text("  ·  Tax Strategy Comparison", M + doc.getTextWidth(who) + 2, 32);
      right(`Tax year ${taxYear}  ·  ${date}`, M + CW, 32);
      hr(40, RULE, 0.6);
    }
    hr(FOOT, RULE, 0.6);
    font(6.8, "normal", SOFT);
    doc.text(doc.splitTextToSize(CYA_TEXT, CW - 70), M, FOOT + 12, { lineHeightFactor: 1.35 });
    font(7.5, "bold", MUTED); right(`Page ${p} of ${total}`, M + CW, FOOT + 12);
  }
  return doc;
}

/** Build the report for the ticked strategies and save it. */
export function generateComparisonReport(all: TaxScenario[], withoutAlt: Record<string, TaxResults>, picked?: number[]): void {
  const doc = buildComparisonReport(all, withoutAlt, picked);
  const name = client(all[0].input.name).replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "") || "Client";
  doc.save(`TaxGenie-${name}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
